/** Ossature de navigation SERVA.
 *  Barre haute en verre, onglets bas en verre sur mobile. AUCUNE SIDEBAR :
 *  l'ecran est trop precieux, et le pouce est en bas. */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, LayoutGrid, LogOut, Plus, Settings, Sun, Users } from "lucide-react";
import { Micro } from "./Bits";

const ONGLETS = [
  { to: "/journee", label: "La Journée", court: "Jour", icone: Sun },
  { to: "/agenda", label: "Agenda", court: "Agenda", icone: CalendarDays },
  { to: "/clients", label: "Clients", court: "Clients", icone: Users },
  { to: "/plus", label: "Plus", court: "Plus", icone: LayoutGrid },
];

import { Marque } from "./Logo";

export { Marque };

export function Shell({ compte, surDeconnexion, surNouveau, children }) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [defile, setDefile] = useState(false);
  const actif = (to) => pathname === to || pathname.startsWith(to + "/");

  useEffect(() => {
    const onScroll = () => setDefile(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* ---------------------------------------- barre haute, en verre */}
      <header
        className={`sticky top-0 z-50 ${defile ? "verre-defile" : "verre"}`}
        style={{ transition: "background-color var(--dur) var(--ease)" }}
      >
        <div className="colonne pad h-[60px] md:h-[64px] flex items-center gap-5">
          <Marque taille={22} />

          {/* onglets ordinateur */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {ONGLETS.map((o) => (
              <Link
                key={o.to}
                to={o.to}
                data-testid={`nav-${o.to.slice(1)}`}
                className="px-3 h-9 inline-flex items-center text-[14px] font-medium"
                style={{
                  borderRadius: "var(--radius-md)",
                  color: actif(o.to) ? "var(--text)" : "var(--text-muted)",
                  background: actif(o.to) ? "var(--accent-soft)" : "transparent",
                  transition: "color var(--dur) var(--ease), background-color var(--dur) var(--ease)",
                }}
              >
                {o.label}
              </Link>
            ))}
          </nav>

          <div className="flex-1 md:hidden min-w-0 text-right">
            <Micro muted className="truncate">{compte?.activite}</Micro>
          </div>

          <button
            onClick={surNouveau}
            data-testid="btn-nouveau-rdv-desktop"
            className="hidden md:inline-flex h-9 px-3.5 items-center gap-1.5 text-[14px] font-semibold"
            style={{
              borderRadius: "var(--radius-md)",
              background: "var(--accent)",
              color: "#fff",
              transition: "background-color var(--dur) var(--ease)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--accent)")}
          >
            <Plus size={15} strokeWidth={2} /> Rendez-vous
          </button>

          <Link
            to="/reglages"
            data-testid="nav-reglages"
            aria-label="Réglages"
            className="hidden md:inline-flex h-9 w-9 items-center justify-center border"
            style={{
              borderRadius: "var(--radius-md)",
              borderColor: actif("/reglages") ? "var(--accent)" : "var(--border)",
              color: actif("/reglages") ? "var(--accent)" : "var(--text-muted)",
            }}
          >
            <Settings size={16} strokeWidth={1.7} />
          </Link>

          <button
            onClick={surDeconnexion}
            data-testid="btn-deconnexion"
            aria-label="Se déconnecter"
            className="hidden md:inline-flex h-9 w-9 items-center justify-center"
            style={{ borderRadius: "var(--radius-md)", color: "var(--text-subtle)" }}
          >
            <LogOut size={16} strokeWidth={1.7} />
          </button>
        </div>
      </header>

      <main className="colonne pad avec-tabbar pt-5 md:pt-7">{children}</main>

      {/* ------------------------------- onglets bas, en verre (mobile) */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 verre-tabbar"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Navigation principale"
      >
        <div className="grid grid-cols-5 h-[58px]">
          <OngletMobile o={ONGLETS[0]} actif={actif(ONGLETS[0].to)} />
          <OngletMobile o={ONGLETS[1]} actif={actif(ONGLETS[1].to)} />
          <button
            onClick={surNouveau}
            data-testid="btn-nouveau-rdv-mobile"
            className="flex items-center justify-center cible-tactile"
            aria-label="Nouveau rendez-vous"
          >
            <span
              className="h-11 w-11 inline-flex items-center justify-center"
              style={{ borderRadius: "var(--radius-md)", background: "var(--accent)", color: "#fff" }}
            >
              <Plus size={21} strokeWidth={2} />
            </span>
          </button>
          <OngletMobile o={ONGLETS[2]} actif={actif(ONGLETS[2].to)} />
          <OngletMobile o={ONGLETS[3]} actif={actif(ONGLETS[3].to) || actif("/finances") || actif("/relances") || actif("/reglages")} />
        </div>
      </nav>
    </div>
  );
}

function OngletMobile({ o, actif }) {
  const Icone = o.icone;
  return (
    <Link
      to={o.to}
      data-testid={`tab-${o.to.slice(1)}`}
      className="relative flex flex-col items-center justify-center gap-1 cible-tactile"
      aria-current={actif ? "page" : undefined}
    >
      {actif && (
        <span
          className="absolute top-0 h-[2px] w-7 rounded-full"
          style={{ background: "var(--accent)" }}
        />
      )}
      <Icone
        size={19}
        strokeWidth={1.7}
        style={{ color: actif ? "var(--accent)" : "var(--text-subtle)" }}
      />
      <span
        className="text-[10px] font-semibold"
        style={{ letterSpacing: "0.02em", color: actif ? "var(--accent)" : "var(--text-subtle)" }}
      >
        {o.court}
      </span>
    </Link>
  );
}
